import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import numeral from 'numeral'
import {
  fetchMonthlyBook, MonthlyBookResponse, MonthlyBookRow, niceCeil,
} from './deliveryChartsContract'

/**
 * Sold / Foundry Booked / Delivered across the Disney goal period (every goal
 * month shows, even before it delivers), in US$ (valued at the inventory rate),
 * as a stacked funnel bar (Delivered ⊆ Booked ⊆ Sold) with three rate lines on a
 * secondary % axis: Delivered÷Booked, Delivered÷Sold, Booked÷Sold.
 * Requested/Booked are MODELED (goals valued then split across months by flight
 * day-share); Delivered is real.
 *
 * The 1H / 2H / Total summary columns render in a separate chart
 * (MonthlyBookTotalsChart) that overrides `selectRows` to use `totals`.
 *
 * ApexCharts 3.44 note: with chart.stacked the LINE series stack too (there is no
 * per-type opt-out in this version). To keep the funnel bars AND show true rate
 * values, the bars are fed as increments (Delivered, Booked−Delivered,
 * Sold−Booked → stack to Sold) and the lines are fed as DE-STACKED
 * increments (db, ds−db, bs−ds → stack back to db, ds, bs). Verified against the
 * real engine. Each bar segment's label shows its own increment; the stack total
 * (= Sold) is on top via plotOptions.bar.dataLabels.total. Tooltip uses the
 * pre-stack actual (cumulative) values below.
 */
const S = {
  delivered: 'Delivered',
  activeBook: 'Foundry Booked',
  requested: 'Sold',
  deliveredBooked: 'Delivered ÷ Booked',
  deliveredSold: 'Delivered ÷ Sold',
  bookedSold: 'Booked ÷ Sold',
}

export default class MonthlyBookChart extends SystemDashboardWidget {
  public title: string = 'Sold / Foundry Booked / Delivered per month'

  public key: string = 'delivery-monthly-book'

  public view: string = 'chart'

  public subView: string = 'bar_stacked'

  public size = { lg: 12, md: 12 }

  public async dataSource(): Promise<MonthlyBookResponse> {
    return fetchMonthlyBook(false)
  }

  /** Rows this chart renders — the goal-period months; the totals chart overrides. */
  protected selectRows(res: MonthlyBookResponse): MonthlyBookRow[] {
    return res.months || []
  }

  protected dataMapper(res: MonthlyBookResponse): Promise<any> {
    const rows: MonthlyBookRow[] = this.selectRows(res)

    const categories = rows.map(r => r.label)

    // Actual (pre-stack) values, for data labels + tooltip.
    const dbA = rows.map(r => r.delivered_booked_rate)
    const dsA = rows.map(r => r.delivered_sold_rate)
    const bsA = rows.map(r => r.booked_sold_rate)
    const db0 = dbA.map(v => v ?? 0)
    const ds0 = dsA.map(v => v ?? 0)
    const bs0 = bsA.map(v => v ?? 0)

    // Cumulative bar values by seriesIndex (0=Delivered,1=Active,2=Requested);
    // actual rate values by seriesIndex (3,4,5) — both keyed for the formatters.
    const cumulative = [rows.map(r => r.delivered), rows.map(r => r.booked), rows.map(r => r.requested)]
    const lineActual: (Array<number | null> | null)[] = [null, null, null, dbA, dsA, bsA]

    const series = [
      { name: S.delivered, type: 'column', data: rows.map(r => r.delivered) },
      { name: S.activeBook, type: 'column', data: rows.map(r => Math.max(0, r.booked - r.delivered)) },
      { name: S.requested, type: 'column', data: rows.map(r => Math.max(0, r.requested - r.booked)) },
      { name: S.deliveredBooked, type: 'line', data: db0 },
      { name: S.deliveredSold, type: 'line', data: ds0.map((v, i) => v - db0[i]) },
      { name: S.bookedSold, type: 'line', data: bs0.map((v, i) => v - ds0[i]) },
    ]

    const barMax = niceCeil(Math.max(0, ...rows.map(r => r.requested)))
    const rateValues = [...db0, ...ds0, ...bs0]
    const rateMax = rateValues.length ? Math.max(0.2, Math.ceil(Math.max(...rateValues) / 0.2) * 0.2) : 1

    // Fixed 2-decimal abbreviation ($1.23m / $109.22k), consistent across all
    // labels (segments, total-on-top, axis) — not the optional-bracket form,
    // which renders a variable number of decimals.
    const usd = (v: number) => numeral(Number.isFinite(v) ? v : 0).format('$0.00a')

    this.chartSettings = {
      chart: {
        type: 'line', stacked: true, height: 400, toolbar: { show: false }, fontFamily: 'inherit',
      },
      colors: ['#0E9F6E', '#F59E0B', '#9CA3AF', '#2563EB', '#7C3AED', '#DB2777'],
      plotOptions: {
        bar: {
          columnWidth: '55%',
          borderRadius: 2,
          // Total (= Sold, the stack sum) on top of each bar. offsetY lifts it
          // clear of the bar so it doesn't read as another segment.
          dataLabels: { total: { enabled: true, offsetY: -10, formatter: (v: number) => usd(v), style: { fontSize: '10px', fontWeight: 700 } } },
        },
      },
      stroke: { width: [0, 0, 0, 3, 3, 3], dashArray: [0, 0, 0, 0, 6, 3], curve: 'smooth' },
      fill: { opacity: [0.95, 0.95, 0.95, 1, 1, 1] },
      markers: { size: [0, 0, 0, 4, 4, 4] },
      // Each bar segment shows its own value: Delivered (full), then the
      // Booked−Delivered and Sold−Booked increments (the plotted values). The
      // Sold total is on top.
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0, 1, 2],
        formatter: (v: number) => (v ? usd(v) : ''),
        style: { fontSize: '9px' },
      },
      legend: { position: 'bottom', horizontalAlign: 'center', onItemClick: { toggleDataSeries: false } },
      xaxis: { categories },
      // One yaxis per series, index-aligned. Bars [0..2] share the left $ scale
      // (stacked → funnel); lines [3..5] the right % scale. Explicit min/max so a
      // tall bar or the 1H Total doesn't clip and the axis never reads NaN.
      yaxis: [
        {
          min: 0, max: barMax, title: { text: 'US$' }, labels: { formatter: (v: number) => usd(v) },
        },
        { min: 0, max: barMax, show: false },
        { min: 0, max: barMax, show: false },
        {
          opposite: true,
          min: 0,
          max: rateMax,
          title: { text: 'Rate' },
          labels: { formatter: (v: number) => (v == null ? '' : numeral(v).format('0%')) },
        },
        {
          opposite: true, min: 0, max: rateMax, show: false,
        },
        {
          opposite: true, min: 0, max: rateMax, show: false,
        },
      ],
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (v: number, o: any) => {
            const i = o.seriesIndex
            if (i <= 2) {
              const val = cumulative[i]?.[o.dataPointIndex]
              return val == null ? '—' : numeral(val).format('$0,0')
            }
            const rate = lineActual[i]?.[o.dataPointIndex]
            return rate == null ? '—' : numeral(rate).format('0.0%')
          },
        },
      },
      grid: { borderColor: '#eef0f3' },
      total: 0,
      labels: [],
    }

    return Promise.resolve(series)
  }
}
